import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-toastify';
import { useAuthContext } from 'context/authContext';
import LoadingButton from 'components/common/LoadingButton';
import { useDonorStripeKey } from 'hooks/useOrganisation';
import {
  useAttachPaymentMethod,
  useCreateStripeCustomer
} from 'hooks/useStripePayments';

// Save a card against a donor's Stripe customer without taking a payment -
// used by the pledges flows so staff aren't sent through Add Donation just to
// capture a card. Creates the Stripe customer first when the donor doesn't
// have one (create-stripe-customer is a no-op for donors that do).
const AddCardForm = ({ donorId, onAdded, onHide }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { organization } = useAuthContext();
  const { attachPaymentMethodAsync } = useAttachPaymentMethod();
  const { createStripeCustomerAsync } = useCreateStripeCustomer();
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    try {
      await createStripeCustomerAsync({
        donorId,
        organisationId: organization?.id
      });

      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement)
      });
      if (result.error) {
        toast.error(result.error.message || 'Invalid card details');
        setSaving(false);
        return;
      }

      await attachPaymentMethodAsync({
        donorId,
        paymentMethodId: result.paymentMethod.id,
        setAsDefault: true
      });

      onAdded(result.paymentMethod);
      onHide();
    } catch (error) {
      // Error toasts handled by the hooks
      console.error('Add card error:', error);
    }
    setSaving(false);
  };

  return (
    <>
      <Modal.Body>
        <Row className="g-3">
          <Col xs={12}>
            <Form.Label>Card number *</Form.Label>
            <div className="form-control">
              <CardNumberElement options={{ showIcon: true }} />
            </div>
          </Col>
          <Col xs={6}>
            <Form.Label>Expiry *</Form.Label>
            <div className="form-control">
              <CardExpiryElement />
            </div>
          </Col>
          <Col xs={6}>
            <Form.Label>CVC *</Form.Label>
            <div className="form-control">
              <CardCvcElement />
            </div>
          </Col>
        </Row>
        <p className="text-600 fs--1 mt-3 mb-0">
          The card is saved securely with Stripe and set as the donor&apos;s
          default — no payment is taken now.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <LoadingButton
          loading={saving}
          disabled={saving || !stripe}
          onClick={handleSave}
        >
          Save Card
        </LoadingButton>
      </Modal.Footer>
    </>
  );
};

AddCardForm.propTypes = {
  donorId: PropTypes.string.isRequired,
  onAdded: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired
};

const AddCardModal = ({ show, onHide, donorId, onAdded }) => {
  // Publishable key from the DONOR's branch (matches the edge function's
  // choice of secret key), org-wide fallback
  const { stripeKey } = useDonorStripeKey(donorId);
  const stripePromise = useMemo(
    () => (stripeKey ? loadStripe(stripeKey) : null),
    [stripeKey]
  );

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Save a Card</Modal.Title>
      </Modal.Header>
      {stripePromise ? (
        <Elements
          stripe={stripePromise}
          options={{ appearance: { theme: 'stripe' } }}
        >
          <AddCardForm donorId={donorId} onAdded={onAdded} onHide={onHide} />
        </Elements>
      ) : (
        <Modal.Body>
          <p className="text-warning mb-0">
            Stripe is not configured for this branch, so cards can&apos;t be
            saved here.
          </p>
        </Modal.Body>
      )}
    </Modal>
  );
};

AddCardModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  donorId: PropTypes.string,
  onAdded: PropTypes.func.isRequired
};

export default AddCardModal;
